import { useMemo } from "react";
import {
  CartesianGrid,
  DotProps,
  Label,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { LineDot } from "recharts/types/cartesian/Line";
import { BaseAxisProps } from "recharts/types/util/types";
import { useImmer } from "use-immer";

import { TimeSeriesColors } from "@intelligems/ig-graphs";
import { GraphConfig, LineData } from "@intelligems/ig-types";
import { AxisTick } from "@src/features/analytics/baseDashboard/chartComponents/AxisTick";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { InteractiveChartLegend } from "@src/features/analytics/legends/InteractiveChartLegend";
import { useSelector } from "@src/store";
import { getExperienceAnalyticsPageName } from "@src/store/slices/dashboardFilters";
import { selectedExperience } from "@src/store/slices/experiences";
import { getSitewidePageName } from "@src/store/slices/sitewideDashboardFilters";
import { theme } from "@src/theme";
import { LineMetricData } from "@src/types/analytics";
import { mpAnalyticsViewChanged } from "@src/utils/mixpanel";
import { getAnalyticsPageType } from "@src/utils/mpUtils";

import { useGetTooltipProps } from "../../hooks/use-get-tooltip-props";
import { formatChartLabel } from "../../utils";

type CustomDotProps = DotProps & {
  data: LineMetricData[];
  id: string;
  index: number;
};

interface IgLineChartProps {
  config: GraphConfig;
  groupMetricData: LineMetricData[];
  externalFocusGroups?: string[];
  syncId?: string;
  lines: LineData[];
  xAxisProps?: BaseAxisProps;
  yAxisProps?: BaseAxisProps;
  dateFormat?: string;
  graphHeight?: number;
}

export const IgLineChart = ({
  config,
  groupMetricData,
  externalFocusGroups,
  syncId,
  lines,
  xAxisProps,
  yAxisProps,
  dateFormat,
  graphHeight,
}: IgLineChartProps) => {
  const graphValueFormatter = useGraphValueFormatter();
  const sitewidePageName = useSelector(getSitewidePageName);
  const expPageName = useSelector(getExperienceAnalyticsPageName);
  const currentExperience = useSelector(selectedExperience);

  const formatTooltipLabel = (label: number) => {
    const shouldUseCurrencyFormatter =
      config?.title?.[0] === "Order Value Distribution" ||
      (config?.title?.[0] === "Cumulative % of Orders" &&
        config?.filterMetric !== "dt");
    if (!shouldUseCurrencyFormatter) return formatChartLabel(label, dateFormat);

    return graphValueFormatter({
      isAxisTick: false,
      value: label,
      configDataTypes: shouldUseCurrencyFormatter
        ? ["currency"]
        : config.dataTypes,
    });
  };

  const [baseFocusGroups, setBaseFocusGroups] = useImmer<string[]>([]);

  const lastDataIndexPerLineId = useMemo(() => {
    const linesId = lines.map((line) => line.id);
    const indexes: Record<string, number> = {};

    groupMetricData.forEach((data, index) => {
      linesId.forEach((lineId) => {
        if (lineId in data) indexes[lineId] = index;
      });
    });

    return indexes;
  }, [groupMetricData, lines]);

  const tooltipProps = useGetTooltipProps({
    dataTypes: config.dataTypes,
    labelFormatting: formatTooltipLabel,
    legends: lines.filter((line) => !baseFocusGroups.includes(line.id)),
    dateFormat,
  });

  const handleLegendClick = (dataKey: string) => {
    if (baseFocusGroups.includes(dataKey)) {
      setBaseFocusGroups(baseFocusGroups.filter((el) => el !== dataKey));
    } else {
      setBaseFocusGroups((prev) => [...prev, dataKey]);
    }
    mpAnalyticsViewChanged({
      pageType: currentExperience?.id
        ? getAnalyticsPageType(currentExperience.category ?? "experiment")
        : "sitewide",
      pageName: currentExperience?.id ? expPageName : sitewidePageName,
      changeType: "chart_filter",
      filterChange: dataKey,
      chartName: config.title?.[0],
    });
  };

  const renderCustomDot = ({
    cx,
    cy,
    data,
    id,
    index,
    stroke,
  }: CustomDotProps) => {
    // The current data entry has data, but not the following one
    // console.log("data.length", data.length);

    if (index === lastDataIndexPerLineId[id]) {
      return <circle cx={cx} cy={cy} r={5} fill={stroke} />;
    }

    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={graphHeight ?? 300}>
      <LineChart
        syncId={syncId}
        data={groupMetricData}
        margin={{
          top: 5,
          bottom: 5,
          left: -20,
        }}
      >
        <CartesianGrid
          style={{ stroke: theme.igBorders.core.disabled }}
          vertical={false}
        />

        <XAxis
          label={
            <Label
              style={{
                fill: theme.palette.text.primary,
                fontFamily: theme.typography.fontFamily,
                fontSize: 12,
              }}
              position={"insideBottom"}
              offset={-5}
              textAnchor={"middle"}
            />
          }
          padding={{ left: 30, right: 30 }}
          tick={(props) => (
            <AxisTick
              config={config}
              graphValueFormatter={graphValueFormatter}
              groupMetricData={groupMetricData}
              dateFormat={dateFormat}
              {...props}
            />
          )}
          interval={config.showEveryXAxisTick ? 0 : undefined}
          axisLine={{ stroke: theme.igBorders.core.disabled }}
          tickLine={false}
          dataKey={"xAxisKey"}
          domain={["auto", "auto"]}
          {...xAxisProps}
        />

        <YAxis
          label={
            <Label
              style={{
                fill: theme.palette.text.primary,
                fontFamily: theme.typography.fontFamily,
                fontSize: 12,
                marginTop: "10px",
                textAnchor: "middle",
              }}
              angle={270}
              position={"insideLeft"}
              offset={10}
              textAnchor={"middle"}
            />
          }
          style={{ fontFamily: theme.typography.fontFamily, fontSize: 10 }}
          axisLine={false}
          tickLine={false}
          allowDecimals={
            !config.dataTypes.every((dataType) =>
              dataType.endsWith("NoDecimal")
            )
          }
          tickFormatter={(value: any) =>
            graphValueFormatter({
              isAxisTick: true,
              value,
              configDataTypes: config.dataTypes,
            })!
          }
          {...yAxisProps}
        />

        <Tooltip {...tooltipProps} />

        {config.showLegend && lines.length > 1 && (
          <Legend
            content={
              <InteractiveChartLegend
                onLegendClick={handleLegendClick}
                focusGroups={baseFocusGroups}
                lines={lines}
              />
            }
            wrapperStyle={{
              paddingTop: 24,
            }}
          />
        )}

        {lines.map(
          ({ animationDuration, color, id, isDotted, showEndDot }, index) => (
            <Line
              // strokeDasharray is not always rendered with active animation
              isAnimationActive={isDotted ? false : true}
              animationDuration={animationDuration}
              connectNulls={false}
              strokeWidth={1.5}
              key={id}
              hide={
                baseFocusGroups.includes(id) ||
                (externalFocusGroups && externalFocusGroups.includes(id))
              }
              dataKey={id}
              dot={
                (showEndDot
                  ? (props) =>
                      renderCustomDot({
                        data: groupMetricData,
                        id: id,
                        ...props,
                      })
                  : false) as LineDot | undefined
              }
              stroke={color ?? TimeSeriesColors[index]}
              strokeDasharray={isDotted ? 2 : 0}
              type="monotone"
            />
          )
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};
